import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { Exports } from '@/pages/Exports'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, LogOut, RotateCcw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

interface ClosureStatus {
  status: string
  closing: boolean
  purge_scheduled_at: string | null
  refund_status: string | null
  refund_amount_cents: number
  refund_currency: string
  can_restore: boolean
  bulk_credits?: {
    erasure_in_progress?: boolean
    retention_review_required: boolean
    available_credits: number
    reserved_credits: number
    warning: string
  }
  requires_checkout_on_restore: boolean
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
}

function formatDate(value: string | null) {
  if (!value) return '30 days after closure was requested'
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(new Date(value))
}

export function AccountClosure() {
  const { user, logout } = useAuth()
  const [status, setStatus] = useState<ClosureStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    api.get('/account/closure-status')
      .then((res) => setStatus(res.data))
      .catch(() => toast.error('We could not load your account closure details.'))
      .finally(() => setLoading(false))
  }, [])

  const restore = async () => {
    setRestoring(true)
    try {
      const res = await api.post('/account/restore')
      toast.success(res.data.message || 'Your account has been restored.')
      window.location.assign(res.data.requires_checkout ? '/billing' : '/')
    } catch (error: any) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Account restoration failed.')
    } finally {
      setRestoring(false)
    }
  }

  const refundProcessing = status?.refund_status === 'pending' || status?.refund_status === 'review'
  const refundComplete = status?.refund_status === 'succeeded'

  if (!loading && status && !status.closing) {
    return <Navigate to="/" replace />
  }

  const deleteImmediately = async () => {
    setDeleteError('')
    setDeleting(true)
    try {
      await api.delete('/account/delete-now', {
        data: { password: deletePassword, confirmation_email: confirmationEmail },
      })
      window.location.assign('/login?account_deleted=1')
    } catch (error: any) {
      const detail = error.response?.data?.detail
      setDeleteError(typeof detail === 'string' ? detail : detail?.message || 'Permanent deletion failed. No data was deleted.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header className="flex items-center justify-between gap-4 border-b pb-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">PurpleToad Mail</p>
            <p className="mt-1 text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </header>

        {loading ? (
          <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : (
          <>
            <section className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
              <div className="space-y-5 py-2">
                <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300">
                  Account closing
                </Badge>
                <div className="space-y-3">
                  <h1 className="max-w-2xl text-4xl font-semibold tracking-tight sm:text-5xl">Your account is closing.</h1>
                  <p className="max-w-2xl text-base leading-7 text-muted-foreground">
                    Read-only until restored. Scheduled deletion: {formatDate(status?.purge_scheduled_at || null)}.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button size="lg" onClick={restore} disabled={!status?.can_restore || restoring}>
                    {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                    {refundComplete ? 'Restore data and choose a plan' : 'Restore account'}
                  </Button>
                  {refundProcessing && <span className="text-sm text-muted-foreground">Restoration unlocks when the refund finishes.</span>}
                </div>
              </div>

              <Card className="self-start">
                <CardHeader><CardTitle className="text-base">Closure status</CardTitle></CardHeader>
                <CardContent className="space-y-5 text-sm">
                  <div>
                    <p className="font-medium">Refund</p>
                    <p className="mt-1 text-muted-foreground">
                      {refundProcessing && 'Your refund is processing. We will email you when it is complete.'}
                      {refundComplete && `${formatMoney(status?.refund_amount_cents || 0, status?.refund_currency || 'USD')} has been refunded.`}
                      {!refundProcessing && !refundComplete && 'There is no refund currently processing.'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">Restore</p>
                    <p className="mt-1 text-muted-foreground">
                      {status?.bulk_credits?.erasure_in_progress ? 'Permanent deletion has started. Restoration is no longer available; retry deletion or contact support if it has not finished.' : refundComplete ? 'Your saved data returns first, then you choose a new plan to reactivate service.' : 'Restore before deletion to return to your account.'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">Permanent deletion</p>
                    <p className="mt-1 text-muted-foreground">Automatic after the recovery window.</p>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="border-t pt-8">
              <div className="mb-5 max-w-2xl">
                <h2 className="text-xl font-semibold tracking-tight">Export your data</h2>
                <p className="mt-1 text-sm text-muted-foreground">{status?.bulk_credits?.erasure_in_progress ? 'Permanent deletion has started. New exports are unavailable.' : 'Download a copy before the deletion date.'}</p>
              </div>
              {!status?.bulk_credits?.erasure_in_progress && <Exports embedded allowDelete={false} />}
            </section>

            <section className="border-t border-destructive/30 py-8">
              <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-end">
                <div className="max-w-2xl">
                  <h2 className="text-xl font-semibold tracking-tight text-destructive">Delete everything now</h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    Permanently remove your account and service data. This cannot be undone. Necessary bulk financial and audit evidence is retained separately under the applicable retention policy.
                  </p>
                  {status?.bulk_credits?.retention_review_required && (
                    <p className="mt-2 text-sm font-medium text-amber-600 dark:text-amber-300">
                      Bulk records need retention review before permanent deletion. Remaining bulk credits become unusable without a refund. Restoring your account does not restart cancelled jobs.
                    </p>
                  )}
                  {refundProcessing && (
                    <p className="mt-2 text-sm font-medium text-amber-600 dark:text-amber-300">
                      Permanent deletion is locked until your refund finishes.
                    </p>
                  )}
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={refundProcessing || status?.bulk_credits?.retention_review_required}
                  className="active:scale-[0.98]"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete account now
                </Button>
              </div>
            </section>
          </>
        )}
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={(open) => !deleting && setShowDeleteDialog(open)}>
        <DialogContent className="border-destructive/40 sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-destructive">Permanently delete your account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="border-l-2 border-destructive pl-4 text-sm leading-6 text-muted-foreground">
              There is no grace period and no recovery. All account and service data will be purged immediately. Any configured domain or mailbox will stop working.
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-confirm-email">Type your account email to confirm</Label>
              <Input
                id="delete-confirm-email"
                type="email"
                autoComplete="off"
                value={confirmationEmail}
                onChange={(event) => setConfirmationEmail(event.target.value)}
                placeholder={user?.email || 'you@example.com'}
                disabled={deleting}
              />
              <p className="text-xs text-muted-foreground">This must exactly match {user?.email}.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-password">Enter your password</Label>
              <Input
                id="delete-password"
                type="password"
                autoComplete="current-password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                disabled={deleting}
              />
              <p className="text-xs text-muted-foreground">Your password is required to authorize this irreversible action.</p>
            </div>
            {deleteError && <p role="alert" className="text-sm text-destructive">{deleteError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>Keep my data</Button>
            <Button
              variant="destructive"
              onClick={deleteImmediately}
              disabled={deleting || confirmationEmail.toLowerCase() !== user?.email.toLowerCase() || !deletePassword}
              className="active:scale-[0.98]"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Permanently delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  )
}
